/* @flow */

import React, { Component } from 'react';
import type { $Suggestion } from 'CoreTypes/AutoCompleteSuggestions';
import { WorkbenchMediaQueries as MQ } from '../../constants/WorkbenchBreakpoints';
import DealersSearchField from '../DealersSearchField/DealersSearchField';
import { debounceFunction } from '../../utils/DataUtils';
import DealersSearchAutocomplete from '../DealersSearchAutocomplete/DealersSearchAutocomplete';

type $Props = {
  mq: string,
  locateMeLabel: string,
  noSuggestionsLabel: string,
  autoSuggestionEnabled: boolean,
  showCountryInSuggestions: boolean,
  onLocateMeButtonClick: Function,
  searchFieldPlaceholderLocation: string,
  searchFieldPlaceholderName: string,
  tabLabelLocation: string,
  tabLabelName: string,
  submitButtonLabel: string,
  marketCode?: string,
  onSubmit: Function,
  getAddressSuggestions: Function,
  instantSearchSuggestions?: boolean
};

type $State = {
  isSearchActive: boolean,
  isSearchByLocationActive: boolean,
  searchLocationString: string,
  searchLocationSuggestions: Array<Object>,
  searchLocationSuggestionsFetched: boolean,
  searchNameString: string,
  hoveredIndex: number
};

const AvailableTabs = {
  TAB_LOCATION: 'TAB_LOCATION',
  TAB_DEALER: 'TAB_DEALER',
}

export default class DealersSearch extends Component<$Props, $State> {

  state = {
    isSearchActive: false,
    isSearchByLocationActive: true,
    searchLocationString: '',
    searchLocationSuggestions: [],
    searchLocationSuggestionsFetched: false,
    searchNameString: '',
    hoveredIndex: -1
  };

  debouncedFetchSuggestions = () => { };

  componentDidMount() {
    this.debouncedFetchSuggestions = this.props.autoSuggestionEnabled ?
      this.props.instantSearchSuggestions ?
        this.fetchSuggestions :
        debounceFunction(this.fetchSuggestions, 200) :
      () => { };
  }

  componentDidUpdate(prevProps: $Props) {
    // This prop is default to TRUE
    // This clause is needed in order to catch its real value and disable the functionality if needed be
    if(prevProps.autoSuggestionEnabled && !this.props.autoSuggestionEnabled) {
      this.debouncedFetchSuggestions = () => {};
    }
  }

  fetchSuggestions = async () => {
    const { searchLocationString } = this.state;

    if (!searchLocationString || searchLocationString.length === 0) {
      this.setState({ searchLocationSuggestions: [], searchLocationSuggestionsFetched: false });
      return;
    }

    const searchLocationSuggestionsResponse = await this.props.getAddressSuggestions({
      address: searchLocationString,
      marketCode: this.props.marketCode,
      maxResults: [MQ.MQ1, MQ.MQ2, MQ.MQ3].includes(this.props.mq) ? 5 : 10
    });
    const searchLocationSuggestions: { suggestions?: Array<$Suggestion> } = searchLocationSuggestionsResponse.data;

    if (searchLocationSuggestions && searchLocationSuggestions.suggestions) {
      const { suggestions } = searchLocationSuggestions;
      this.setState({
        hoveredIndex: suggestions.length > 0 ? 0 : -1,
        searchLocationSuggestionsFetched: true,
        searchLocationSuggestions: this.props.showCountryInSuggestions ?
          suggestions :
          suggestions.map(suggestion => ({
            ...suggestion,
            label: suggestion.label.substring(suggestion.label.indexOf(', ') + 1, suggestion.label.length).trim()
          }))
      });
    } else {
      this.setState({ searchLocationSuggestions: [], hoveredIndex: -1, searchLocationSuggestionsFetched: true });
    }
  };

  handleTabClick = (isSearchByLocationActive: boolean) => {
    this.setState({ isSearchByLocationActive });
  };

  handleSearchCriteriaLocationChange = (searchLocationString: string) => {
    this.setState({ searchLocationString }, this.debouncedFetchSuggestions);
  };

  handleSearchCriteriaNameChange = (searchNameString: string) => {
    this.setState({ searchNameString });
  };

  submitSearch = (selectedAutoSuggestion?: $Suggestion) => {
    const { isSearchByLocationActive, searchLocationString, searchNameString } = this.state;
    const safeSearchForName = !isSearchByLocationActive && searchNameString.trim() !== '';
    const safeSearchForLocation = isSearchByLocationActive && searchLocationString.trim() !== '';
    if (safeSearchForName || safeSearchForLocation) {
      this.props.onSubmit(
        this.state.isSearchByLocationActive,
        this.state.searchLocationString,
        this.state.searchNameString,
        selectedAutoSuggestion
      );
      this.setState({
        isSearchActive: false,
        searchLocationString: selectedAutoSuggestion? selectedAutoSuggestion.label : this.state.searchLocationString
      },
      //Fetch suggestions with the new input, i.e., the selected selectedAutoSuggestion.label
      this.debouncedFetchSuggestions);
    }
    if (searchNameString.trim() === '') {
      this.setState({
        searchNameString: ''
      })
    }
  }

  onSearchFocus = () => {
    this.setState({ isSearchActive: true });
  };

  onSearchBlur = () => {
    this.setState({
      isSearchActive: false,
      hoveredIndex: this.state.searchLocationSuggestions.length > 0 ? 0 : -1
    },
    () => this.scrollSuggestionIntoView(0, false, true));
  };

  changeHoveredSuggestionIndex = (hoveredIndex: number) => {
    this.setState({
      hoveredIndex
    });
  }

  scrollSuggestionIntoView = (hoveredIndex: number, scrollDown: boolean, scrollToTop: boolean) => {
    const suggestionElem = document.getElementById(`dlc_suggestion_${hoveredIndex}`);
    if (suggestionElem) {
      const scrollerElement = suggestionElem.parentElement;
      if(scrollerElement) {
        const currentScrollTop = scrollerElement.scrollTop;
        const currentScrollBottom = currentScrollTop + scrollerElement.clientHeight;

        const suggestionPositionTop = suggestionElem.offsetTop;
        const suggestionPositionBottom = suggestionPositionTop + suggestionElem.clientHeight;

        //Knowing the suggestion position, we need to guarantee that the element is fully visible
        //i.e., both top and bottom of the element are in the scroll interval
        const suggestionIsVisible = currentScrollTop < suggestionPositionTop &&
          suggestionPositionBottom < currentScrollBottom;

        if(!suggestionIsVisible && scrollerElement.scrollTo) {
          //if suggestion is not visible, scroll to its position
          const scrollToGo = scrollDown ?
            currentScrollTop + suggestionElem.clientHeight :
            currentScrollTop - suggestionElem.clientHeight;
          // $FlowFixMe
          scrollerElement.scrollTo({
            top: scrollToTop? 0 : scrollToGo
          })
        }
      }
    }
  }

  handleKeyboardInputForAutoSuggest = (event: SyntheticInputEvent<HTMLInputElement>, formWasSubmitted: boolean) => {
    const { hoveredIndex, searchLocationSuggestions } = this.state;
    if (formWasSubmitted) {
      this.handleEnterKeyPressedInSearchInput(event);
    } else {
      // $FlowFixMe
      switch (event.keyCode) {
        case 9: //event.key = Tab
          if (hoveredIndex >= -1 && !event.shiftKey) {
            event.preventDefault();
            //Hover to search by geolocation
            this.setState({
              hoveredIndex: 99
            });
          } else if (hoveredIndex === 99) {
            if (event.shiftKey) event.preventDefault();
            this.setState({
              hoveredIndex: searchLocationSuggestions.length > 0 ? 0 : -1
            }, () => this.scrollSuggestionIntoView(0, false, true));
          }
          break;

        case 13: //event.key = Enter
          event.preventDefault();
          event.stopPropagation();
          this.handleEnterKeyPressedInSearchInput(event);
          break;

        case 40: //event.key = ArrowDown
          if (hoveredIndex > -1 && hoveredIndex < searchLocationSuggestions.length - 1) {
            this.setState({
              hoveredIndex: hoveredIndex + 1
            });
            this.scrollSuggestionIntoView(hoveredIndex + 1, true, false);
          }
          event.preventDefault();
          break;

        case 38: //event.key = ArrowUp
          event.preventDefault();
          if (hoveredIndex > 0) {
            this.setState({
              hoveredIndex: hoveredIndex - 1
            });
            this.scrollSuggestionIntoView(hoveredIndex - 1, false, false);
          }
          break;

        default:
          break;
      }
    }
  }

  handleEnterKeyPressedInSearchInput = (event: SyntheticInputEvent<HTMLInputElement>) => {
    const { hoveredIndex, searchLocationSuggestions, isSearchByLocationActive } = this.state;

    event.preventDefault();
    if (isSearchByLocationActive && this.props.autoSuggestionEnabled) {
      if (hoveredIndex >= 0 && hoveredIndex < searchLocationSuggestions.length) {
        this.submitSearch(searchLocationSuggestions[hoveredIndex]);
      } else if (hoveredIndex === 99) {
        this.props.onLocateMeButtonClick();
      }
    } else {
      this.submitSearch();
    }
  }

  render() {
    const { isSearchByLocationActive, searchLocationString, hoveredIndex } = this.state;
    const { searchFieldPlaceholderLocation, searchFieldPlaceholderName, autoSuggestionEnabled } = this.props;
    const baseClassName = 'dl-dealers-search';
    const inputPlaceholder = isSearchByLocationActive ? searchFieldPlaceholderLocation : searchFieldPlaceholderName;

    return (
      <form className={`${baseClassName}__form`} onSubmit={ e => this.handleKeyboardInputForAutoSuggest(e, true) }>
        { this.props.tabLabelLocation && this.props.tabLabelName && (
          <div className={`${baseClassName}__tabs`}>
            <wb-tabs selected={isSearchByLocationActive ? AvailableTabs.TAB_LOCATION : AvailableTabs.TAB_DEALER}>
              <wb-tab-bar>
                  <wb-tab
                    name={AvailableTabs.TAB_LOCATION}
                    key={AvailableTabs.TAB_LOCATION}
                    selected={isSearchByLocationActive}
                    onClick={ (event: SyntheticInputEvent<HTMLInputElement>) => {
                      event.preventDefault();
                      this.handleTabClick(true);
                    }}
                  >
                    {this.props.tabLabelLocation}
                  </wb-tab>
                  <wb-tab
                    name={AvailableTabs.TAB_DEALER}
                    key={AvailableTabs.TAB_DEALER}
                    selected={!isSearchByLocationActive}
                    onClick={ (event: SyntheticInputEvent<HTMLInputElement>) => {
                      event.preventDefault();
                      this.handleTabClick(false);
                    }}
                  >
                    {this.props.tabLabelName}
                  </wb-tab>
              </wb-tab-bar>
            </wb-tabs>
          </div>
          )
        }

        <DealersSearchField
          mq={ this.props.mq }
          isSearchByLocationActive={ isSearchByLocationActive }
          searchLocationString={ this.state.searchLocationString }
          searchNameString={ this.state.searchNameString }
          inputFieldPlaceholder={ inputPlaceholder }
          submitLabel={ this.props.submitButtonLabel }
          autoSuggestionEnabled={ autoSuggestionEnabled }
          hoveredIndex={ hoveredIndex }
          onSearchCriteriaLocationChange={ this.handleSearchCriteriaLocationChange }
          onSearchCriteriaNameChange={ this.handleSearchCriteriaNameChange }
          onSearchFocus={ this.onSearchFocus }
          onSearchBlur={ this.onSearchBlur }
          handleKeyboardInput = { this.handleKeyboardInputForAutoSuggest }
        />

        <DealersSearchAutocomplete
          mq={ this.props.mq }
          marketCode={ this.props.marketCode }
          locateMeLabel={ this.props.locateMeLabel }
          noSuggestionsLabel= { this.props.noSuggestionsLabel }
          isSearchByLocationActive={ isSearchByLocationActive }
          isSearchActive={ this.state.isSearchActive }
          autoSuggestionEnabled={ autoSuggestionEnabled }
          showCountryInSuggestions={ this.props.showCountryInSuggestions }
          baseClassName={ baseClassName }
          searchLocationString={ searchLocationString }
          searchLocationSuggestions={ this.state.searchLocationSuggestions }
          hoveredIndex={ hoveredIndex }
          searchLocationSuggestionsFetched={ this.state.searchLocationSuggestionsFetched }
          searchNameString={ this.state.searchNameString }
          getAddressSuggestions={ this.props.getAddressSuggestions }
          submitSearch={ this.submitSearch }
          onLocateMeButtonClick={ this.props.onLocateMeButtonClick }
          onSuggestionHoverChange={ this.changeHoveredSuggestionIndex }
        />

      </form>
    );
  }
}
